import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Reports.css';
import { FaPlus, FaTimes, FaTrash, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Reports = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (taskId, e) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === taskId ? null : taskId);
  };

  const handleCancel = async (taskId, uuid) => {
    if (window.confirm(`Are you sure you want to cancel task ID=${taskId}?`)) {
      try {
        await axios.post(`/api/v1/cancel?uuid=${uuid}`);
      } catch (err) {
        console.error('Error cancelling task:', err);
      } finally {
        fetchTasks();
      }
    }
  };

  const handleDelete = async (taskId, uuid) => {
    if (window.confirm(`Are you sure you want to PERMANENTLY DELETE task ID=${taskId}? \n\nThis action will remove all associated records and CANNOT be undone.`)) {
      try {
        await axios.post(`/api/v1/delete?uuid=${uuid}`);
        setOpenDropdownId(null);
      } catch (err) {
        console.error('Error deleting task:', err);
        alert('Failed to delete task. Please try again.');
      } finally {
        fetchTasks();
      }
    }
  };

  const fetchTasks = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    }
    try {
      const response = await axios.get('/api/v1/tasks');
      if (response.data && Array.isArray(response.data.data)) {
        setTasks(response.data.data);
        setError(null);
      } else {
        if (isInitialLoad) {
          setError('Failed to fetch tasks. Please try again later.');
        }
        console.error('Invalid data format received from server.');
      }
    } catch (err) {
      if (isInitialLoad) {
        setError('Failed to fetch tasks. Please try again later.');
      }
      console.error('Error fetching tasks:', err);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchTasks(true);
    const interval = setInterval(() => {
      fetchTasks();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="tasks-container"><p>Loading tasks...</p></div>;
  }

  if (error) {
    return <div className="tasks-container"><p>{error}</p></div>;
  }

  return (
    <div className="tasks-container">
      <h2>Reports</h2>
      <table className="tasks-table">
        <thead>
          <tr>
            <th className="col-task-id">Task ID</th>
            <th className="col-project-name">Project Name</th>
            <th className="col-doc-info">Document Info</th>
            <th className="col-report">Report</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.id}>
              <td className="col-task-id" title={task.id}>{task.id}</td>
              <td className="col-project-name" title={task.project_nickname}>{task.project_nickname || 'Untitled'}</td>
              <td className="col-doc-info doc-info-cell">
                <div className="doc-files-section">
                  {task.doc_name_en && (
                    <div className="doc-info-item" title={task.doc_name_en}>
                      <span className="doc-info-label">EN</span> 
                      <span className="doc-info-text">{task.doc_name_en}</span>
                    </div>
                  )}
                  <div className="doc-info-item" title={task.doc_name_cn}>
                    <span className="doc-info-label">CN</span> 
                    <span className="doc-info-text">{task.doc_name_cn}</span>
                  </div>
                </div>
                
                <div className="doc-timestamps-section">
                  <div className="timestamp-item" title={`Created At: ${new Date(task.created_at).toLocaleString()}`}>
                    <span className="timestamp-label">SUBMITTED</span>
                    <span className="timestamp-value">{new Date(task.created_at).toLocaleString()}</span>
                  </div>
                  {task.execute_begin && (
                    <div className="timestamp-item" title={`Started At: ${new Date(task.execute_begin).toLocaleString()}`}>
                      <span className="timestamp-label">STARTED</span>
                      <span className="timestamp-value">{new Date(task.execute_begin).toLocaleString()}</span>
                    </div>
                  )}
                  {parseInt(task.progress, 10) === 100 && (task.executed_end || task.last_modified_at) && (
                    <div className="timestamp-item" title={`Completed At: ${new Date((task.executed_end && task.executed_end !== 0 && task.executed_end !== '0') ? task.executed_end : task.last_modified_at).toLocaleString()}`}>
                      <span className="timestamp-label">COMPLETED</span>
                      <span className="timestamp-value">{new Date((task.executed_end && task.executed_end !== 0 && task.executed_end !== '0') ? task.executed_end : task.last_modified_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </td>
              <td>
                {!task.executed_by ? (
                  <div className="queuing-status">
                    Queuing
                    <button 
                      className="btn-cancel" 
                      onClick={() => handleCancel(task.id, task.uuid)}
                      title="Cancel Task"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : parseInt(task.progress, 10) === 100 ? (
                  <div className="report-action-group" ref={openDropdownId === task.id ? dropdownRef : null}>
                    <div className="btn-group">
                      <a 
                        href={`/api/v1/result?uuid=${task.uuid}`} 
                        className={`btn-report ${!task.enable_delete ? 'btn-report-single' : ''}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        View Report
                      </a>
                      {task.enable_delete && (
                        <button 
                          className={`btn-dropdown-trigger ${openDropdownId === task.id ? 'active' : ''}`}
                          onClick={(e) => toggleDropdown(task.id, e)}
                          title="More options"
                        >
                          <FaChevronDown />
                        </button>
                      )}
                    </div>
                    {openDropdownId === task.id && (
                      <div className="dropdown-menu">
                        <button 
                          className="dropdown-item delete-option"
                          onClick={() => handleDelete(task.id, task.uuid)}
                        >
                          <FaTrash className="dropdown-icon" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${task.progress}%` }}>
                      {task.progress}%
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="auto-refresh-note">This page automatically refreshes every 30 seconds.</p>
    </div>
  );
};

export default Reports;
